<script setup lang="ts">
import {storeToRefs} from "pinia";
import {useSubscriptionPlanStore} from "@/stores/subscription-plan.store";
import {useExtensionStore} from "@/stores/extension.store";
import {computed} from "vue";
import {SubscriptionPlan} from "@/subscription-plan/interfaces";
import SubscriptionPlanExtensionGrid from "@/home/components/SubscriptionPlanExtensionGrid.vue";
import ProgramExtensionGrid from "@/home/components/ProgramExtensionGrid.vue";
import Extension from "@/core/interfaces/extension/Extension";
import {useRoute, useRouter} from "vue-router";
import ExtensionNavigationService from "@/extension/services/ExtensionNavigationService";
import HomeSponsoredExtensions from "@/home/components/HomeSponsoredExtensions.vue";
import {ExtensionGridSkeleton} from "@/extension/components/extension-grid";
import ShowAllExtensionCard from "@/home/components/ShowAllExtensionCard.vue";
import SubscriptionPlanService from "@/subscription-plan/services/SubscriptionPlanService";

const subscriptionPlanStore = useSubscriptionPlanStore();
const extensionStore = useExtensionStore();
const {globalPlan, individualPlans, loading: subscriptionPlansLoading} = storeToRefs(subscriptionPlanStore);
const {enabledProgramExtensions, loading: extensionsLoading} = storeToRefs(extensionStore);
const router = useRouter();
const route = useRoute();

const status = computed(() => {
    if (!globalPlan.value) {
        return null;
    }

    if (SubscriptionPlanService.hasActiveSubscription(globalPlan.value)) {
        return 'subscribed';
    }

    const latestTrial = globalPlan.value.latest_trial;

    if (!latestTrial) {
        return 'no-trial';
    }

    return latestTrial.status;
});

/**
 * The subscription plan that are linked to enabled extensions.
 */
const subscriptionPlanFromEnabledExtensions = computed(() => {
    const mappedPlans = enabledProgramExtensions.value.map(extension => {
        if (extension?.subscription_plan) {
            return extension.subscription_plan;
        }
    }).filter(plan => !!plan) as SubscriptionPlan[];

    return individualPlans.value.filter(plan => {
        return mappedPlans.some(mappedPlan => mappedPlan.id === plan.id);
    });
});

/**
 * The program extensions without a subscription plan.
 */
const orphanProgramExtensions = computed(() => {
    return enabledProgramExtensions.value.filter(
        extension => !extension.subscription_plan && extension.organization.entity_type !== 'hospital'
    );
});

/**
 * Navigate to the extension.
 */
const navigateToExtension = (extension: Extension) => {
    ExtensionNavigationService.navigate(extension, router, route);
};

const loading = computed(() => {
    return (subscriptionPlansLoading.value || extensionsLoading.value);
});
</script>

<template>
  <div class="space-y-6">
    <div class="bg-[#DDDDF5] p-4 rounded-lg">
      <div class="flex gap-4 justify-between">
        <div>
          <h1>Libérez votre potentiel avec nos modules</h1>
          <p>Activez des modules pour alerter les patients ciblés au comptoir</p>
        </div>
        <router-link :to="{name: 'extension-store'}">
          <ShowAllExtensionCard />
        </router-link>
      </div>
      <div
        v-if="status === 'active'|| status === 'no-trial' || status === 'subscribed'"
        class="border-l-[8px] border-primary"
      >
        <HomeSponsoredExtensions class="px-2">
          <template #title>
            <h1 class="text-black">
              Modules partenaires
            </h1>
          </template>
        </HomeSponsoredExtensions>
      </div>
    </div>

    <div>
      <h1 class="px-4">
        Vos modules activés
      </h1>
      <div class="bg-white p-4 rounded-lg">
        <div v-if="loading" class="space-y-2 mt-2">
          <ExtensionGridSkeleton v-for="(_, index) in 2" :key="index" :count="2" logo />
        </div>
        <div v-else class="space-y-8 mt-2">
          <div v-if="!subscriptionPlanFromEnabledExtensions?.length && !orphanProgramExtensions?.length">
            <p>
              ⚠️ Vous n’avez aucun module actif. Activez des modules pour reconnaitre et conseiller les patients au
              comptoir.
            </p>
          </div>
          <div v-else>
            <SubscriptionPlanExtensionGrid
              v-for="plan in subscriptionPlanFromEnabledExtensions ?? []"
              :key="plan.id"
              appends-icon
              enabled
              :plan="plan"
              @extension-click="navigateToExtension"
            />
            <ProgramExtensionGrid
              appends-icon
              :program-extensions="orphanProgramExtensions"
              @extension-click="navigateToExtension"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>

</style>
